import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Sparkles, ArrowRight, FileText, Download, ShieldCheck } from 'lucide-react';

export default function PaymentSuccessPage({ 
  user, 
  onNavigate, 
  orderId = '', 
  paymentId = '', 
  planType = 'premium_799',
  submissionId = null
}) {
  // Fire Google Ads conversion if gtag is loaded on window
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': window.GOOGLE_ADS_CONVERSION_ID || undefined,
          'value': planType === 'premium_799' ? 799.0 : 49.0,
          'currency': 'INR',
          'transaction_id': paymentId || orderId || undefined
        });
        console.log('[Google Ads] Purchase conversion event dispatched for URL: /payment-success');
      }
    } catch (e) {
      console.warn('[Tracking] Conversion event exception:', e);
    }
  }, [paymentId, orderId, planType]);

  const isPremium = planType === 'premium_799';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-10 space-y-8 shadow-2xl shadow-indigo-950/40 text-center"
      >
        {/* Animated Success Badge */}
        <div className="flex flex-col items-center justify-center space-y-3">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-xl shadow-emerald-500/20">
            <CheckCircle2 className="w-10 h-10 sm:w-12 sm:h-12" />
          </div>
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-400 bg-emerald-950/60 px-3 py-1 rounded-full border border-emerald-500/30">
            Payment Verified & Confirmed
          </span>
          <h1 className="text-2xl sm:text-4xl font-black text-white">
            Thank You for Your Order!
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 max-w-md mx-auto">
            Your transaction has been processed successfully. Your account has been upgraded with full ATS optimization access.
          </p>
        </div>

        {/* Receipt Card */}
        <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-5 text-left space-y-3">
          <div className="flex justify-between items-center border-b border-slate-800 pb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Order Summary</span>
            <span className="text-xs font-bold text-emerald-400 flex items-center gap-1">
              <ShieldCheck className="w-4 h-4" /> Paid via Razorpay
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
            <div>
              <span className="block text-[11px] text-slate-500">Plan Activated</span>
              <span className="font-semibold text-slate-200">
                {isPremium ? 'Premium Monthly (20 Resumes)' : 'Pay Per Resume (Single)'}
              </span>
            </div>
            <div className="text-right">
              <span className="block text-[11px] text-slate-500">Amount Paid</span>
              <span className="font-bold text-indigo-400 text-base">
                ₹{isPremium ? '799' : '49'}
              </span>
            </div>
            {paymentId && (
              <div>
                <span className="block text-[11px] text-slate-500">Payment ID</span>
                <span className="font-mono text-slate-300 text-xs truncate block max-w-[180px]">{paymentId}</span>
              </div>
            )}
            {orderId && (
              <div className="text-right">
                <span className="block text-[11px] text-slate-500">Order Reference</span>
                <span className="font-mono text-slate-300 text-xs truncate block max-w-[180px]">{orderId}</span>
              </div>
            )}
          </div>

          {user && (
            <div className="pt-2 border-t border-slate-800/80 text-[11px] text-slate-400 flex justify-between">
              <span>Account: <b>{user.email}</b></span>
              <span>Available Quota: <b className="text-emerald-400">{user.resumes_left || (isPremium ? 20 : 1)} Resumes</b></span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          {submissionId ? (
            <button
              onClick={() => onNavigate('/templates', { sub_id: submissionId })}
              className="py-3.5 px-6 rounded-xl font-bold text-sm bg-indigo-600 hover:bg-indigo-500 text-white transition flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/30 cursor-pointer"
            >
              <Download className="w-4 h-4" />
              <span>Choose Template & Download PDF</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => onNavigate('/')}
              className="py-3.5 px-6 rounded-xl font-bold text-sm bg-indigo-600 hover:bg-indigo-500 text-white transition flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/30 cursor-pointer"
            >
              <Sparkles className="w-4 h-4" />
              <span>Tailor Your Resume Now</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}

          <button
            onClick={() => onNavigate('/my-resumes')}
            className="py-3.5 px-6 rounded-xl font-bold text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 transition flex items-center justify-center gap-2 border border-slate-700 cursor-pointer"
          >
            <FileText className="w-4 h-4" />
            <span>Go to My Resumes</span>
          </button>
        </div>

        <p className="text-[11px] text-slate-500">
          A confirmation receipt has also been dispatched to your registered email address.
        </p>
      </motion.div>
    </div>
  );
}
